"use client";

import React, {JSX, useLayoutEffect, useRef} from "react";
import type {HTMLInputAutoCompleteAttribute} from "react";

import {useSnapshot} from "valtio";

import {useScrollIntoView} from "@/shared/hooks/dom/use-scroll-into-view";
import {useTranslation} from "@/shared/i18n";
import {type FieldInstance} from "@/shared/models/form/fields";

import "./styles/textarea-field.scss";

interface TextareaFieldProps {
    model: FieldInstance;
    id?: string;
    label?: string;
    labelVariant?: "default" | "subtle";
    placeholder?: string;
    fontSize?: "default" | "small" | "flexible";
    className?: string;
    rows?: number;
    autoComplete?: HTMLInputAutoCompleteAttribute;
}

export function TextareaField({
    model,
    id,
    label,
    labelVariant = "default",
    placeholder,
    fontSize = "default",
    className,
    rows = 4,
    autoComplete = "on",
}: TextareaFieldProps): JSX.Element {
    const {t} = useTranslation("validation");
    const snapshot = useSnapshot(model);
    const scrollRef = useScrollIntoView<HTMLTextAreaElement>(model);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Combine refs
    const setRefs = (element: HTMLTextAreaElement | null): void => {
        (scrollRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = element;
        textareaRef.current = element;
    };

    // Restore cursor position after render (from model)
    useLayoutEffect(() => {
        if (!textareaRef.current) return;
        if (!snapshot.cursorPosition) return;

        const {start, end} = snapshot.cursorPosition;
        textareaRef.current.setSelectionRange(start, end);
        model.clearCursorPosition();
    }, [snapshot.value, snapshot.cursorPosition, model]);

    const labelClass = labelVariant === "subtle" ? "textarea-field__label--subtle" : "";
    const fontSizeClass =
        fontSize === "small" ? "textarea-field__input--font-small" : fontSize === "flexible" ? "textarea-field__input--font-flexible" : "";

    return (
        <div className={`textarea-field ${className || ""}`}>
            {label && (
                <label htmlFor={id} className={`textarea-field__label ${labelClass}`}>
                    {label} {model.isRequired && <span className="textarea-field__required">*</span>}
                </label>
            )}

            <textarea
                ref={setRefs}
                id={id}
                rows={rows}
                value={snapshot.value}
                onChange={(e) => {
                    // Save cursor position before setValue (for restoration after transform)
                    model.setCursorPosition(e.target.selectionStart ?? 0, e.target.selectionEnd ?? 0);
                    model.setValue(e.target.value);
                }}
                onBlur={() => model.onBlur()}
                onFocus={() => model.onFocus()}
                disabled={snapshot.disabled}
                placeholder={placeholder}
                autoComplete={autoComplete}
                className={`textarea-field__input ${fontSizeClass} ${snapshot.shouldShowError ? "has-error" : ""}`}
            />

            {snapshot.shouldShowError && snapshot.error && (
                <span className="textarea-field__error-text">
                    {t(snapshot.error.message as Parameters<typeof t>[0], snapshot.error.messageParams)}
                </span>
            )}
        </div>
    );
}
